import React, { useState, useEffect } from 'react';
import { usePlayer } from '../../context/PlayerContext';
import { Filter, Calendar, Users, MapPin, X, ChevronDown, ChevronUp } from 'lucide-react';

const StatsFilter = ({ filters, setFilters, games }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [opponents, setOpponents] = useState([]);
  const [locations, setLocations] = useState([]);
  const { activeSeason } = usePlayer();

  // Extract unique opponents and locations from games
  useEffect(() => {
    if (games && games.length > 0) {
      // Get unique opponents
      const uniqueOpponents = [...new Set(games.map(game => game.opponent).filter(Boolean))];
      setOpponents(uniqueOpponents);

      // Get unique locations
      const uniqueLocations = [...new Set(games.map(game => game.location).filter(Boolean))];
      setLocations(uniqueLocations);
    }
  }, [games]);

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      gameId: null,
      dateRange: 'all',
      opponent: 'all',
      location: 'all',
      customDateStart: null,
      customDateEnd: null
    });
  };

  // Get date range options
  const getDateRangeOptions = () => {
    const options = [
      { value: 'all', label: 'All Games' },
      { value: 'last7', label: 'Last 7 Days' },
      { value: 'last30', label: 'Last 30 Days' },
      { value: 'custom', label: 'Custom Range' }
    ];

    // Add season option if we have an active season
    if (activeSeason) {
      options.splice(1, 0, { value: 'season', label: `${activeSeason.name} Season` });
    }

    return options;
  };

  return (
    <div className="mb-4 bg-white dark:bg-dark-200 rounded-lg shadow-sm border border-gray-200 dark:border-dark-300">
      {/* Filter header - always visible */}
      <div 
        className="p-3 flex justify-between items-center cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center">
          <Filter size={18} className="text-blue-600 dark:text-blue-400 mr-2" />
          <h3 className="font-medium text-gray-800 dark:text-gray-200">Filter Statistics</h3>
          
          {/* Show active filter count if any filters are applied */}
          {(filters.gameId || 
            filters.dateRange !== 'all' || 
            filters.opponent !== 'all' || 
            filters.location !== 'all') && (
            <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full">
              Active
            </span>
          )}
        </div>
        
        <div className="flex items-center">
          {/* Clear filters button - only show if filters are applied */}
          {(filters.gameId || 
            filters.dateRange !== 'all' || 
            filters.opponent !== 'all' || 
            filters.location !== 'all') && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                clearFilters();
              }}
              className="mr-3 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Clear
            </button>
          )}
          
          {/* Expand/collapse icon */}
          {isExpanded ? (
            <ChevronUp size={18} className="text-gray-500 dark:text-gray-400" />
          ) : (
            <ChevronDown size={18} className="text-gray-500 dark:text-gray-400" />
          )}
        </div>
      </div>
      
      {/* Expandable filter options */}
      {isExpanded && (
        <div className="p-3 border-t border-gray-200 dark:border-dark-300">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Date Range Filter */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar size={16} className="mr-1" />
                Date Range
              </label>
              <select
                value={filters.dateRange}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-dark-500 rounded-md bg-white dark:bg-dark-300 text-gray-800 dark:text-gray-200"
              >
                {getDateRangeOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              
              {/* Custom date range inputs */}
              {filters.dateRange === 'custom' && (
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={filters.customDateStart || ''}
                      onChange={(e) => handleFilterChange('customDateStart', e.target.value)}
                      className="w-full p-2 border border-gray-300 dark:border-dark-500 rounded-md bg-white dark:bg-dark-300 text-gray-800 dark:text-gray-200"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={filters.customDateEnd || ''}
                      onChange={(e) => handleFilterChange('customDateEnd', e.target.value)}
                      className="w-full p-2 border border-gray-300 dark:border-dark-500 rounded-md bg-white dark:bg-dark-300 text-gray-800 dark:text-gray-200"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Opponent Filter */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Users size={16} className="mr-1" />
                Opponent
              </label>
              <select
                value={filters.opponent}
                onChange={(e) => handleFilterChange('opponent', e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-dark-500 rounded-md bg-white dark:bg-dark-300 text-gray-800 dark:text-gray-200"
              >
                <option value="all">All Opponents</option>
                {opponents.map(opponent => (
                  <option key={opponent} value={opponent}>
                    {opponent}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Location Filter */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <MapPin size={16} className="mr-1" />
                Location
              </label>
              <select
                value={filters.location}
                onChange={(e) => handleFilterChange('location', e.target.value)}
                className="w-full p-2 border border-gray-300 dark:border-dark-500 rounded-md bg-white dark:bg-dark-300 text-gray-800 dark:text-gray-200"
              >
                <option value="all">All Locations</option>
                <option value="home">Home Games</option>
                <option value="away">Away Games</option>
                {locations.map(location => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Specific Game Filter */}
            <div>
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Calendar size={16} className="mr-1" />
                Specific Game
              </label>
              <select
                value={filters.gameId || ''}
                onChange={(e) => handleFilterChange('gameId', e.target.value || null)}
                className="w-full p-2 border border-gray-300 dark:border-dark-500 rounded-md bg-white dark:bg-dark-300 text-gray-800 dark:text-gray-200"
              >
                <option value="">All Games</option>
                {games && games.map(game => (
                  <option key={game.id} value={game.id}>
                    {game.date} - {game.opponent || 'Unknown Opponent'}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsFilter;
